import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from '../Img/logo.png';
import { Menu, X, ChevronRight } from "lucide-react"; 

const Navbar = () => {
  const location = useLocation();
  const [active, setActive] = useState(location.pathname);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (location) {
      setActive(location.pathname);
      setIsMobileMenuOpen(false);
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // 1. IMPROVED: Background Scroll Lock Logic
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      // Prevent mobile bounce effect on body
      document.body.style.touchAction = "none"; 
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }

    // Cleanup function when component unmounts
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleBlogClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
            const section = document.getElementById("blog-section");
            if (section) section.scrollIntoView({ behavior: "smooth" });
        }, 500);
    } else {
        const section = document.getElementById("blog-section");
        if (section) section.scrollIntoView({ behavior: "smooth" });
    }
    setActive("blog");
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { title: "Home", href: "/", ariaLabel: "Go to Home Page" },
    { title: "Games", href: "/games", ariaLabel: "Play Free Online Games" },
    { title: "About", href: "/about", ariaLabel: "About Game Zone Hub" },
    { title: "Privacy", href: "/privacy-policy", ariaLabel: "Read Privacy Policy" },
    { title: "Contact", href: "/contact", ariaLabel: "Contact Game Zone Hub Support" },
    { title: "Terms", href: "/terms", ariaLabel: "Read Terms and Conditions" },
  ];

  return (
    <>
      <nav className="bg-gray-900/90 lg:px-8 text-white shadow-lg fixed w-full top-0 z-50 backdrop-blur-md border-b border-gray-700 transition-all duration-300">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            
            <Link to="/" className="flex items-center space-x-3 cursor-pointer group">
              <img src={logo} className="w-12 h-12 text-purple-500 object-cover group-hover:scale-110 transition-transform duration-300" width="48" height="48" alt="GameZone Hub" />
                <span className="font-bold text-2xl tracking-wider bg-gradient-to-r from-[#9b4dff] via-[#e056fd] to-[#00eaff] text-transparent bg-clip-text">
                  GameZone
                </span>
            </Link>

            <ul className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navLinks.map((element) => {
                let isActive = active === element.href;
                return (  
                  <li key={element.title} className="list-none">
                   <Link 
                      to={element.href} 
                      aria-label={element.ariaLabel}
                      className={`${isActive ? 'text-cyan-400 font-bold' : 'text-gray-300 font-medium'} hover:text-purple-400 transition-colors duration-300 text-sm lg:text-base`} 
                    >
                      {element.title}
                    </Link>
                  </li>
                 )
              })}
              <li className="list-none">
                  <a href="#blog-section" onClick={handleBlogClick} aria-label="Read our Gaming Blog" className="text-gray-300 font-medium hover:text-purple-400 transition-colors duration-300 text-sm lg:text-base cursor-pointer">
                      Blog
                  </a>
              </li>
            </ul>

            <button 
              className="md:hidden text-gray-300 hover:text-white focus:outline-none p-2"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open Mobile Menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu size={30} />
            </button>
          </div>
        </div>
      </nav>

      {/* Backdrop */}
      <div 
        className={`fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
        style={{ touchAction: "none" }} // Prevents scrolling on backdrop
      ></div>

      {/* 2. IMPROVED: Offcanvas Menu using Flexbox for proper inner scrolling */}
      <div 
        className={`fixed top-0 right-0 z-[70] h-[100dvh] w-[280px] bg-gray-900 border-l border-gray-700 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation Menu"
      >
        {/* Header (Fixed at top) */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-gray-800">
          <span className="font-bold text-xl bg-gradient-to-r from-[#9b4dff] to-[#00eaff] text-transparent bg-clip-text">
            Menu
          </span>
          <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
            aria-label="Close Mobile Menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content (Flex-1 allows it to take remaining space) */}
        <div className="flex-1 overflow-y-auto overscroll-contain py-4 px-3 space-y-2">
          {navLinks.map((element) => {
             let isActive = active === element.href;
             return (
               <Link 
                 key={element.title}
                 to={element.href}
                 aria-label={element.ariaLabel}
                 className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group ${
                   isActive 
                     ? 'bg-blue-600/10 text-cyan-400 border border-blue-500/30' 
                     : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                 }`}
               >
                 <span className="font-medium text-lg">{element.title}</span>
                 <ChevronRight size={18} className={`transition-transform duration-200 ${isActive ? 'text-cyan-400' : 'text-gray-600 group-hover:translate-x-1'}`} />
               </Link>
             )
          })}
          
          <a 
             href="#blog-section" 
             aria-label="Read our Gaming Blog"
             onClick={handleBlogClick}
             className="flex items-center justify-between px-4 py-3 rounded-xl text-gray-300 hover:bg-gray-800 hover:text-white transition-all duration-200 group"
           >
             <span className="font-medium text-lg">Blog & News</span>
             <ChevronRight size={18} className="text-gray-600 group-hover:translate-x-1" />
           </a>
        </div>

        {/* Footer (Fixed at bottom) */}
        <div className="flex-shrink-0 p-6 text-center border-t border-gray-800 bg-gray-900">
          <p className="text-xs text-gray-500">© {new Date().getFullYear()} Game Zone Hub</p>
        </div>
      </div>
    </>
  );
};

export default Navbar;